import { Markdown } from "@/components/Markdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { likertIndexOf, likertValueFor } from "@/lib/questionType";
import { cn } from "@/lib/utils";

interface LikertProps {
  /** The statement, verbatim from the IPIP item pool. */
  promptMd: string;
  /** The five response labels, in value order — always from the server. */
  options: string[];
  /** The candidate's VALUE, "1"–"5" (null = unanswered). */
  selected: string | null;
  onSelect: (value: string) => void;
  /** The IPIP instruction line, shown above every statement. */
  instructions?: string | null;
}

/**
 * EF-9 — one questionnaire statement on a 1–5 accuracy scale.
 *
 * A native radio group (arrow keys move, Space picks) so it is keyboard- and
 * screen-reader-complete; each option is a full-width 44 px+ row carrying its
 * number AND its label, so the choice never rests on colour or position
 * alone, and the list stacks cleanly at 375 px. The candidate never sees what
 * a statement measures — the server sends no scale, key or instrument.
 */
export function Likert({
  promptMd,
  options,
  selected,
  onSelect,
  instructions,
}: LikertProps) {
  const selectedIndex = likertIndexOf(selected);
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Card>
        <CardHeader className="space-y-1.5">
          {instructions && (
            <p className="text-sm text-muted-foreground">{instructions}</p>
          )}
          <CardTitle as="h1">How accurately does this describe you?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-lg leading-relaxed">
            <Markdown source={promptMd} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <RadioGroup
            value={selected ?? ""}
            onValueChange={onSelect}
            aria-label="Your answer"
            className="grid gap-2"
          >
            {options.map((label, idx) => {
              const value = likertValueFor(idx);
              const id = `likert-opt-${idx}`;
              const isSelected = selectedIndex === idx;
              return (
                <Label
                  key={id}
                  htmlFor={id}
                  className={cn(
                    "flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-4 py-3 transition-colors duration-150",
                    "hover:bg-accent",
                    isSelected ? "border-primary bg-primary/5" : "border-border",
                  )}
                >
                  <RadioGroupItem id={id} value={value} />
                  <span
                    className={cn(
                      "w-5 shrink-0 font-mono text-sm tabular-nums",
                      isSelected ? "text-primary" : "text-muted-foreground",
                    )}
                    aria-hidden="true"
                  >
                    {value}
                  </span>
                  <span className="text-base leading-snug">{label}</span>
                </Label>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
}

export default Likert;
